import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  ArrowLeft, DollarSign, Wallet, Clock, CheckCircle2, 
  XCircle, AlertTriangle, Loader2, CreditCard, Bitcoin, Building
} from 'lucide-react';
import { formatCurrency } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Withdrawals = () => {
  const { challengeId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [availableData, setAvailableData] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [formData, setFormData] = useState({
    amount: '',
    payment_method: 'bank_transfer',
    payment_details: '',
    // Bank transfer details
    bank_name: '',
    account_holder_name: '',
    account_number: '',
    sort_code: '',
    iban: '',
    swift_bic: '',
    // Crypto details
    wallet_address: '',
    crypto_network: 'TRC20',
    // PayPal details
    paypal_email: ''
  });

  useEffect(() => {
    fetchData();
  }, [challengeId]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch available withdrawal amount
      const availableRes = await fetch(`${API_URL}/api/withdrawals/available/${challengeId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (availableRes.ok) {
        const data = await availableRes.json();
        setAvailableData(data);
      }

      // Fetch withdrawal history
      const historyRes = await fetch(`${API_URL}/api/withdrawals/my`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (historyRes.ok) {
        const data = await historyRes.json();
        setWithdrawals(data.filter(w => w.challenge_id === challengeId));
      }
    } catch (error) {
      toast.error('Failed to load withdrawal data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (parseFloat(formData.amount) > availableData.available_for_withdrawal) {
      toast.error('Amount exceeds available balance');
      return;
    }

    if (parseFloat(formData.amount) < 50) {
      toast.error('Minimum withdrawal is $50');
      return;
    }

    if (!availableData.is_verified) {
      toast.error('Please complete KYC verification first');
      navigate('/kyc');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      
      // Build payment details based on method
      let paymentDetails = {};
      if (formData.payment_method === 'bank_transfer') {
        paymentDetails = {
          bank_name: formData.bank_name,
          account_holder_name: formData.account_holder_name,
          account_number: formData.account_number,
          sort_code: formData.sort_code,
          iban: formData.iban,
          swift_bic: formData.swift_bic
        };
      } else if (formData.payment_method === 'crypto') {
        paymentDetails = {
          wallet_address: formData.wallet_address,
          network: formData.crypto_network
        };
      } else if (formData.payment_method === 'paypal') {
        paymentDetails = {
          paypal_email: formData.paypal_email
        };
      }
      
      const response = await fetch(`${API_URL}/api/withdrawals/request`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          challenge_id: challengeId,
          amount: parseFloat(formData.amount),
          payment_method: formData.payment_method,
          payment_details: JSON.stringify(paymentDetails)
        })
      });

      if (response.ok) {
        toast.success('Withdrawal request submitted! Awaiting admin approval.');
        setFormData({ 
          amount: '', 
          payment_method: 'bank_transfer', 
          payment_details: '',
          bank_name: '',
          account_holder_name: '',
          account_number: '',
          sort_code: '',
          iban: '',
          swift_bic: '',
          wallet_address: '',
          crypto_network: 'TRC20',
          paypal_email: ''
        });
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit withdrawal');
      }
    } catch (error) {
      toast.error('Failed to submit withdrawal');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-500/20 text-yellow-400"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
      case 'approved':
        return <Badge className="bg-green-500/20 text-green-400"><CheckCircle2 className="w-3 h-3 mr-1" /> Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-500/20 text-red-400"><XCircle className="w-3 h-3 mr-1" /> Rejected</Badge>;
      case 'completed':
        return <Badge className="bg-blue-500/20 text-blue-400"><CheckCircle2 className="w-3 h-3 mr-1" /> Completed</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getMethodIcon = (method) => {
    switch (method) {
      case 'crypto':
        return <Bitcoin className="w-4 h-4" />;
      case 'bank_transfer':
        return <Building className="w-4 h-4" />;
      default:
        return <CreditCard className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center pt-16">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-20 pb-10 px-4" data-testid="withdrawals-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="font-heading text-3xl font-bold text-foreground">Withdraw Funds</h1>
          <p className="text-muted-foreground mt-2">Request a withdrawal from your funded account profits</p>
        </motion.div>

        {/* Rules Banner */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-4 bg-purple-500/10 rounded-lg border border-purple-500/30"
        >
          <h3 className="font-heading text-sm font-bold text-purple-400 mb-2">PAYOUT RULES</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground text-xs">First Payout After</p>
              <p className="font-mono font-bold">{availableData?.rules?.min_trading_days_for_first_payout || 6} trading days</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">Processing Time</p>
              <p className="font-mono font-bold">{availableData?.rules?.processing_time_hours || 24} hours</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">Min Withdrawal</p>
              <p className="font-mono font-bold">${availableData?.rules?.min_withdrawal || 50}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">After First Payout</p>
              <p className="font-mono font-bold">Every {availableData?.rules?.withdrawal_frequency_days || 14} days</p>
            </div>
          </div>
          
          {/* Trading days progress */}
          {availableData?.is_first_payout && (
            <div className="mt-4 p-3 bg-secondary/30 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground">Trading Days Progress</span>
                <span className="text-xs font-mono">
                  {availableData?.trading_days || 0} / {availableData?.rules?.min_trading_days_for_first_payout || 6} days
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all ${availableData?.can_request_payout ? 'bg-green-500' : 'bg-yellow-500'}`}
                  style={{ 
                    width: `${Math.min(100, ((availableData?.trading_days || 0) / (availableData?.rules?.min_trading_days_for_first_payout || 6)) * 100)}%` 
                  }}
                />
              </div>
              {!availableData?.can_request_payout && (
                <p className="text-xs text-yellow-400 mt-2">
                  ⏳ {availableData?.trading_days_remaining || 0} more trading day(s) required before first payout
                </p>
              )}
            </div>
          )}

          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm border-t border-purple-500/20 pt-4">
            <div>
              <p className="text-muted-foreground text-xs">Max Drawdown</p>
              <p className="font-mono font-bold">{availableData?.rules?.max_drawdown_percent || 15}%</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">Daily Drawdown</p>
              <p className="font-mono font-bold">{availableData?.rules?.daily_drawdown_percent || 8}% (Trailing)</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            ⚠️ <strong>Important:</strong> You can only withdraw profits. Account balance must remain at or above starting balance (${formatCurrency(availableData?.starting_balance || 0)}).
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Available Balance Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:col-span-1"
          >
            <Card className="bg-gradient-to-br from-green-500/20 to-emerald-500/10 border-green-500/30">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2 text-green-400">
                  <Wallet className="w-5 h-5" />
                  Withdrawable Profits
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground">Your Share ({availableData?.profit_split_percent || 80}%)</p>
                  <p className="font-mono text-3xl font-bold text-green-400">
                    {formatCurrency(availableData?.available_for_withdrawal || 0)}
                  </p>
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Starting Balance</span>
                    <span className="font-mono">{formatCurrency(availableData?.starting_balance || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Current Balance</span>
                    <span className="font-mono">{formatCurrency(availableData?.current_balance || 0)}</span>
                  </div>
                  <div className="flex justify-between border-t border-border pt-2">
                    <span className="text-muted-foreground">Total Profit</span>
                    <span className="font-mono text-green-400">{formatCurrency(availableData?.total_profit || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Your Share ({availableData?.profit_split_percent || 80}%)</span>
                    <span className="font-mono">{formatCurrency(availableData?.your_share || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Already Withdrawn</span>
                    <span className="font-mono">{formatCurrency(availableData?.total_withdrawn || 0)}</span>
                  </div>
                </div>

                {!availableData?.is_verified && (
                  <div className="p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-yellow-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-yellow-400 font-medium">KYC Required</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Complete identity verification to withdraw funds.
                        </p>
                        <Button
                          size="sm"
                          className="mt-2"
                          onClick={() => navigate('/kyc')}
                        >
                          Verify Now
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Withdrawal Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-2"
          >
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="font-heading text-lg">New Withdrawal Request</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label>Amount (USD)</Label>
                    <div className="relative mt-2">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type="number"
                        placeholder="Enter amount"
                        value={formData.amount}
                        onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                        className="pl-10 font-mono"
                        min="50"
                        max={availableData?.available_for_withdrawal || 0}
                        step="0.01"
                        data-testid="withdrawal-amount"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Minimum: $50 | Maximum: {formatCurrency(availableData?.available_for_withdrawal || 0)}
                    </p>
                  </div>

                  <div>
                    <Label>Payment Method</Label>
                    <Select
                      value={formData.payment_method}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, payment_method: value }))}
                    >
                      <SelectTrigger className="mt-2" data-testid="payment-method-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bank_transfer">
                          <div className="flex items-center gap-2">
                            <Building className="w-4 h-4" />
                            Bank Transfer
                          </div>
                        </SelectItem>
                        <SelectItem value="crypto">
                          <div className="flex items-center gap-2">
                            <Bitcoin className="w-4 h-4" />
                            Cryptocurrency (USDT)
                          </div>
                        </SelectItem>
                        <SelectItem value="paypal">
                          <div className="flex items-center gap-2">
                            <CreditCard className="w-4 h-4" />
                            PayPal
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Bank Transfer Details */}
                  {formData.payment_method === 'bank_transfer' && (
                    <div className="space-y-4 p-4 rounded-lg bg-white/5 border border-white/10">
                      <h4 className="font-medium text-white flex items-center gap-2">
                        <Building className="w-4 h-4 text-cyan-400" />
                        Bank Account Details
                      </h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-gray-400 text-sm">Bank Name *</Label>
                          <Input
                            placeholder="e.g. Barclays, Chase"
                            value={formData.bank_name}
                            onChange={(e) => setFormData(prev => ({ ...prev, bank_name: e.target.value }))}
                            className="mt-1 bg-white/5 border-white/10"
                            required
                          />
                        </div>
                        <div>
                          <Label className="text-gray-400 text-sm">Account Holder Name *</Label>
                          <Input
                            placeholder="Full name on account"
                            value={formData.account_holder_name}
                            onChange={(e) => setFormData(prev => ({ ...prev, account_holder_name: e.target.value }))}
                            className="mt-1 bg-white/5 border-white/10"
                            required
                          />
                        </div>
                        <div>
                          <Label className="text-gray-400 text-sm">Account Number *</Label>
                          <Input
                            placeholder="e.g. 12345678"
                            value={formData.account_number}
                            onChange={(e) => setFormData(prev => ({ ...prev, account_number: e.target.value }))}
                            className="mt-1 bg-white/5 border-white/10"
                            required
                          />
                        </div>
                        <div>
                          <Label className="text-gray-400 text-sm">Sort Code / Routing Number *</Label>
                          <Input
                            placeholder="e.g. 12-34-56 or 123456789"
                            value={formData.sort_code}
                            onChange={(e) => setFormData(prev => ({ ...prev, sort_code: e.target.value }))}
                            className="mt-1 bg-white/5 border-white/10"
                            required
                          />
                        </div>
                        <div>
                          <Label className="text-gray-400 text-sm">IBAN (International)</Label>
                          <Input
                            placeholder="e.g. GB82 WEST 1234 5698 7654 32"
                            value={formData.iban}
                            onChange={(e) => setFormData(prev => ({ ...prev, iban: e.target.value }))}
                            className="mt-1 bg-white/5 border-white/10"
                          />
                        </div>
                        <div>
                          <Label className="text-gray-400 text-sm">SWIFT/BIC Code</Label>
                          <Input
                            placeholder="e.g. BARCGB22"
                            value={formData.swift_bic}
                            onChange={(e) => setFormData(prev => ({ ...prev, swift_bic: e.target.value }))}
                            className="mt-1 bg-white/5 border-white/10"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Crypto Details */}
                  {formData.payment_method === 'crypto' && (
                    <div className="space-y-4 p-4 rounded-lg bg-white/5 border border-white/10">
                      <h4 className="font-medium text-white flex items-center gap-2">
                        <Bitcoin className="w-4 h-4 text-yellow-400" />
                        Cryptocurrency Details
                      </h4>
                      <div className="space-y-4">
                        <div>
                          <Label className="text-gray-400 text-sm">Network *</Label>
                          <Select
                            value={formData.crypto_network}
                            onValueChange={(value) => setFormData(prev => ({ ...prev, crypto_network: value }))}
                          >
                            <SelectTrigger className="mt-1 bg-white/5 border-white/10">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="TRC20">USDT (TRC20 - Tron)</SelectItem>
                              <SelectItem value="ERC20">USDT (ERC20 - Ethereum)</SelectItem>
                              <SelectItem value="BEP20">USDT (BEP20 - BSC)</SelectItem>
                              <SelectItem value="BTC">Bitcoin (BTC)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-gray-400 text-sm">Wallet Address *</Label>
                          <Input
                            placeholder="Enter your wallet address"
                            value={formData.wallet_address}
                            onChange={(e) => setFormData(prev => ({ ...prev, wallet_address: e.target.value }))}
                            className="mt-1 bg-white/5 border-white/10 font-mono text-sm"
                            required
                          />
                          <p className="text-xs text-yellow-400 mt-1">
                            ⚠️ Double-check your address. Funds sent to wrong address cannot be recovered.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* PayPal Details */}
                  {formData.payment_method === 'paypal' && (
                    <div className="space-y-4 p-4 rounded-lg bg-white/5 border border-white/10">
                      <h4 className="font-medium text-white flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-blue-400" />
                        PayPal Details
                      </h4>
                      <div>
                        <Label className="text-gray-400 text-sm">PayPal Email *</Label>
                        <Input
                          type="email"
                          placeholder="your@email.com"
                          value={formData.paypal_email}
                          onChange={(e) => setFormData(prev => ({ ...prev, paypal_email: e.target.value }))}
                          className="mt-1 bg-white/5 border-white/10"
                          required
                        />
                      </div>
                    </div>
                  )}

                  <div className="p-4 bg-secondary/30 rounded-lg">
                    <p className="text-sm text-muted-foreground">
                      <strong>Note:</strong> All withdrawals require admin approval. Processing time is typically 1-3 business days after approval.
                    </p>
                  </div>

                  <Button
                    type="submit"
                    className="w-full font-heading uppercase tracking-wider"
                    disabled={submitting || !availableData?.is_verified}
                    data-testid="submit-withdrawal"
                  >
                    {submitting ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <DollarSign className="w-4 h-4 mr-2" />
                    )}
                    Request Withdrawal
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Withdrawal History */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-8"
        >
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Withdrawal History</CardTitle>
            </CardHeader>
            <CardContent>
              {withdrawals.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No withdrawal requests yet</p>
              ) : (
                <div className="space-y-3">
                  {withdrawals.map((withdrawal) => (
                    <div
                      key={withdrawal.id}
                      className="flex items-center justify-between p-4 bg-secondary/30 rounded-lg"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          {getMethodIcon(withdrawal.payment_method)}
                        </div>
                        <div>
                          <p className="font-mono font-bold">{formatCurrency(withdrawal.amount)}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(withdrawal.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      {getStatusBadge(withdrawal.status)}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default Withdrawals;
